/**
 * Pure-function date formatting aligned with the legacy `ConvertIsoDateService` + `UtilService.dateFormatString`.
 *
 * The user-chosen date format is stored in `sessionStorage.userEnv → date_format`
 * (moment-style tokens such as `DD.MM.YYYY`).  Default: `DD.MM.YYYY`.
 */

let cachedUserDateFormat: string | null = null;

/** Moment-style pattern from `sessionStorage.userEnv` (default `DD.MM.YYYY`), cached until {@link invalidateUserDateFormatCache}. */
export function getUserDateFormat(): string {
  if (cachedUserDateFormat != null) return cachedUserDateFormat;
  let format: string;
  try {
    const raw = sessionStorage.getItem('userEnv');
    format = raw ? JSON.parse(raw)?.date_format || 'DD.MM.YYYY' : 'DD.MM.YYYY';
  } catch {
    format = 'DD.MM.YYYY';
  }
  cachedUserDateFormat = format;
  return format;
}

/** Clears the {@link getUserDateFormat} cache. Call whenever `sessionStorage.userEnv` is written. */
export function invalidateUserDateFormatCache(): void {
  cachedUserDateFormat = null;
}

/** Map moment-style tokens (`DD.MM.YYYY`) to PrimeNG `p-datepicker` pattern (`dd.mm.yy`).
 *  PrimeNG uses jQuery-UI tokens: `yy` = 4-digit year, `y` = 2-digit year. */
export function momentFormatToPrimeNg(pattern: string): string {
  return pattern
    .replace(/YYYY/g, 'yy')
    .replace(/YY/g, 'y')
    .replace(/DD/g, 'dd')
    .replace(/MM/g, 'mm')
    .replace(/D/g, 'd')
    .replace(/M/g, 'm');
}

/** Lowercase placeholder hint matching the moment pattern (e.g. `dd.mm.yyyy`). */
export function momentFormatToPlaceholder(pattern: string): string {
  return pattern.toLowerCase();
}

/** Map moment-style tokens (`DD.MM.YYYY`) to PrimeNG InputMask pattern (`99.99.9999`). */
export function momentFormatToInputMask(pattern: string): string {
  return pattern
    .replace(/YYYY/g, '9999')
    .replace(/YY/g, '99')
    .replace(/DD/g, '99')
    .replace(/MM/g, '99')
    .replace(/D/g, '9')
    .replace(/M/g, '9');
}

function pad(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}

function formatDateByPattern(d: Date, pattern: string): string {
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());

  switch (pattern.toLowerCase()) {
    case 'dd.mm.yyyy':
      return `${day}.${month}.${year}`;
    case 'dd-mm-yyyy':
      return `${day}-${month}-${year}`;
    case 'yyyy/mm/dd':
      return `${year}/${month}/${day}`;
    case 'mm/dd/yyyy':
      return `${month}/${day}/${year}`;
    case 'dd/mm/yyyy':
    case 'd/mm/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-mm-dd':
      return `${year}-${month}-${day}`;
    default:
      return `${day}.${month}.${year}`;
  }
}

function formatTime(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function toDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const d = typeof value === 'number' ? new Date(value) : new Date(value as any);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a date (or timestamp) using the user's configured date pattern. */
export function formatKlDateValue(value: unknown, dateTime = false): string {
  const d = toDate(value);
  if (!d) return '';
  const pattern = getUserDateFormat();
  const dateStr = formatDateByPattern(d, pattern);
  return dateTime ? `${dateStr}  ${formatTime(d)}` : dateStr;
}

/** Format only HH:mm:ss. */
export function formatKlTimeOnly(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  return formatTime(d);
}

/** Format a date for grid filter caption (date-only). */
export function formatKlDateCaption(value: unknown): string {
  return formatKlDateValue(value, false);
}

/** Format a date-time for grid filter caption. */
export function formatKlDateTimeCaption(value: unknown): string {
  return formatKlDateValue(value, true);
}
